import { Calendar } from "@fluentui/react-calendar-compat";
import { Button, Popover, PopoverSurface, PopoverTrigger } from "@fluentui/react-components";
import { CalendarMonth20Regular, Dismiss20Regular } from "@fluentui/react-icons";
import { useCallback, useEffect, useMemo, useState } from "react";

interface AnalysisDatePickerProps {
    value?: string;
    onChange: (value: string) => void;
}

const calendarStrings = {
    months: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
    shortMonths: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
    days: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
    shortDays: ["S", "M", "T", "W", "T", "F", "S"],
    goToToday: "Go to today",
    prevMonthAriaLabel: "Go to previous month",
    nextMonthAriaLabel: "Go to next month",
    prevYearAriaLabel: "Go to previous year",
    nextYearAriaLabel: "Go to next year",
    prevYearRangeAriaLabel: "Go to previous year range",
    nextYearRangeAriaLabel: "Go to next year range",
    monthPickerHeaderAriaLabel: "{0}, select to change the year",
    yearPickerHeaderAriaLabel: "{0}, select to change the month",
    closeButtonAriaLabel: "Close calendar",
    selectedDateFormatString: "Selected date {0}",
    todayDateFormatString: "Today's date {0}",
    weekNumberFormatString: "Week number {0}",
    dayMarkedAriaLabel: "Marked day",
};

export default function AnalysisDatePicker({ value = "", onChange }: AnalysisDatePickerProps) {
    const [selectedDate, setSelectedDate] = useState<Date | null>(value ? new Date(value) : null);
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        setSelectedDate(value ? new Date(value) : null);
    }, [value]);

    const handleDateChange = useCallback((date: Date | null | undefined) => {
        const safeDate = date || null;
        setSelectedDate(safeDate);
        onChange(safeDate ? safeDate.toLocaleDateString("sv-SE") : "");
        setIsOpen(false);
    }, [onChange]);

    const displayValue = useMemo(() => selectedDate ? selectedDate.toLocaleDateString("sv-SE") : "", [selectedDate]);

    const clearDate = useCallback(() => {
        setSelectedDate(null);
        onChange("");
        setIsOpen(false);
    }, [onChange]);

    return (
        <div className="analysis-order-custom-date-picker mt-1">
            <style>{`
                .analysis-order-custom-date-picker .analysis-order-date-input input {
                    background-color: transparent !important;
                    border: 0;
                    color: inherit !important;
                    -webkit-text-fill-color: currentColor !important;
                    box-shadow: none;
                }

                .analysis-order-custom-date-picker .analysis-order-date-input input::placeholder {
                    color: inherit !important;
                    opacity: 1;
                }

                .analysis-order-custom-date-picker .analysis-order-date-input input:focus {
                    outline: none;
                }

                .analysis-order-custom-date-picker .analysis-order-date-action {
                    background-color: transparent !important;
                    color: inherit !important;
                }

                .analysis-order-custom-date-picker .analysis-order-calendar-surface {
                    padding: 0;
                }
            `}</style>
            <Popover open={isOpen} onOpenChange={(_, data) => setIsOpen(data.open)} positioning="below-start">
                <PopoverTrigger disableButtonEnhancement>
                    <div className="analysis-order-date-input flex min-h-10 w-full items-center border border-primary-dark/70 bg-primary-light px-3 text-primary-dark dark:border-primary-light/50 dark:bg-oi-dark-background dark:text-oi-powder-form-yellow">
                        <input
                            type="text"
                            readOnly
                            value={displayValue}
                            placeholder={new Date().toLocaleDateString("sv-SE")}
                            className="h-full w-full flex-1 cursor-pointer bg-transparent text-sm placeholder:text-primary-dark/65 dark:placeholder:text-primary-light/72"
                            onClick={() => setIsOpen(true)}
                        />
                        {displayValue && (
                            <Button
                                type="button"
                                appearance="transparent"
                                className="analysis-order-date-action min-w-0 px-1"
                                icon={<Dismiss20Regular />}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    clearDate();
                                }}
                                aria-label="Clear date"
                            />
                        )}
                        <Button
                            type="button"
                            appearance="transparent"
                            className="analysis-order-date-action min-w-0 px-1"
                            icon={<CalendarMonth20Regular />}
                            onClick={(event) => {
                                event.stopPropagation();
                                setIsOpen(current => !current);
                            }}
                            aria-label="Open calendar"
                        />
                    </div>
                </PopoverTrigger>
                <PopoverSurface className="analysis-order-calendar-surface border border-primary-dark/70 bg-primary-light text-primary-dark dark:border-primary-light/50 dark:bg-oi-dark-background dark:text-oi-powder-form-yellow">
                    <Calendar
                        value={selectedDate ?? new Date()}
                        onSelectDate={handleDateChange}
                        strings={calendarStrings}
                        showGoToToday={true}
                    />
                </PopoverSurface>
            </Popover>
        </div>
    );
}
